import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ExternalLink, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { api } from '../services/api'
import Button from '../components/Button'

export default function PagePreview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = new URLSearchParams(window.location.search)
  const locale = urlParams.get('locale') || 'en'
  const viewMode = urlParams.get('view_mode') || 'live'

  const isPost = location.pathname.startsWith('/posts/')
  const entityType = isPost ? 'post' : 'page'
  const entityTypePlural = isPost ? 'posts' : 'pages'

  const {
    data: previewData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [entityType + '-preview', id, locale, viewMode],
    queryFn: () =>
      api
        .get(`/${entityTypePlural}/${id}/preview`, {
          params: { locale, view_mode: viewMode },
        })
        .then((r) => r.data),
    enabled: !!id,
  })

  const content = previewData?.[entityType] || previewData?.page
  const blocks = previewData?.blocks || []
  const [copied, setCopied] = useState(false)

  const apiBaseUrl = import.meta.env.VITE_API_URL || '/api/v1'
  const fullApiBaseUrl = apiBaseUrl.startsWith('http') ? apiBaseUrl : `${window.location.origin}${apiBaseUrl}`
  const contentSlug = content?.slug || ''
  const publicEndpoint = contentSlug 
    ? `${fullApiBaseUrl}/content/${locale}/${entityTypePlural}/${contentSlug}`
    : null

  const copyEndpoint = () => {
    if (publicEndpoint) {
      navigator.clipboard.writeText(publicEndpoint)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const renderBlockContent = (block) => {
    if (!block.content || typeof block.content !== 'object') {
      return <div className="text-gray-500 italic">No content</div>
    }

    return Object.entries(block.content).map(([fieldKey, fieldValue]) => {
      if (!fieldValue) return null

      if (typeof fieldValue === 'string') {
        return (
          <div key={fieldKey} className="mb-2">
            <div className="text-sm font-medium text-gray-600 mb-1">
              {fieldKey
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </div>
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: fieldValue }}
            />
          </div>
        )
      }

      if (typeof fieldValue === 'object' && fieldValue !== null) {
        return (
          <div key={fieldKey} className="mb-2">
            <div className="text-sm font-medium text-gray-600 mb-1">
              {fieldKey
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </div>
            <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
              {JSON.stringify(fieldValue, null, 2)}
            </pre>
          </div>
        )
      }

      return null
    })
  }

  if (!id) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Invalid Content ID
          </h2>
          <p className="text-gray-500 mb-4">
            The content ID in the URL is invalid or missing.
          </p>
          <Button onClick={() => navigate(`/${entityTypePlural}`)}>
            Go to{' '}
            {entityTypePlural.charAt(0).toUpperCase() +
              entityTypePlural.slice(1)}
          </Button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading preview...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Preview
          </h2>
          <p className="text-gray-500 mb-4">
            {error.message || 'An error occurred while loading the preview.'}
          </p>
          <Button onClick={() => navigate(`/${entityTypePlural}/${id}`)}>
            Back to {isPost ? 'Post' : 'Page'} Builder
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/${entityTypePlural}/${id}`)}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Editor</span>
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {isPost ? 'Post' : 'Page'} Preview JSON
              </h1>
              <p className="text-sm text-gray-500">
                {content?.title} ({locale.toUpperCase()}, {viewMode})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(window.location.href, '_blank')}
              className="flex items-center space-x-2"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Open in New Tab</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              REST API Endpoint
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-900 text-green-400 px-4 py-3 rounded-lg text-sm font-mono overflow-x-auto">
                {publicEndpoint ? `GET ${publicEndpoint}` : 'No slug available'}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={copyEndpoint}
                className="shrink-0"
                disabled={!publicEndpoint}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {content?.permalink && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frontend Permalink
              </label>
              <code className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg text-sm font-mono overflow-x-auto">
                {content.permalink}
              </code>
            </div>
          )}
        </div>
        <pre className="bg-gray-100 p-6 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(previewData, null, 2)}
        </pre>
      </div>
    </div>
  )
}
